import { observable } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
import {
  IAdvisorUserCompanyUpdateRequest,
  ICompany,
  ICompanyForm,
  ICompanyUsersRequest,
} from '../types';
import { CREDIT_PROFILE } from '../utils/enums/generic.enum';
import { Auth, Connect, Message, SocketObservable } from '@genesislcap/foundation-comms';

export interface ICompanyService {
  getAllCompaniesAdmin(criteria?: string): Promise<Message<any>>;
  setCompany(): void;
  getCurrentCompany(): ICompany;
  isInvestmentGrade(): boolean;
  isCreditWorthy(): boolean;
  clearCompany(): void;
  getAllUserCompanies(): SocketObservable<Message>;
  getAllCompanies(criteria?: string): SocketObservable<Message>;
  getAllCompaniesShanpshot(criteria?: string): Promise<Message<any>>;
  getMyCompanies(criteria?: string): SocketObservable<Message>;
  updateCurrentCompany(cascadeCompanyId: string): Promise<any>;
  eventCompanyInsert(company: ICompanyForm): Promise<any>;
  eventCompanyUpdate(company: ICompanyForm): Promise<any>;
  acceptCascadeAgreement(companyId?: string): Promise<any>;
  canAutoExecute(): boolean;
  eventREmapOpen(): Promise<any>;
  getBuyerAdvisorCompany(): Promise<Message<any>>;
  getAdvisorUsersByCompany(buyerCompanyId: string, advisorCompanyId): Promise<Message<any>>;
  eventAdvisorUserCompanyUpdate(request: IAdvisorUserCompanyUpdateRequest): Promise<any>;
  eventAssignCompanyUsers(request: ICompanyUsersRequest): Promise<any>;
  eventUnassignCompanyUsers(request: ICompanyUsersRequest): Promise<any>;
  isNonSefParticipantAgreement(): boolean;
  isSefParticipantAgreement(): boolean;
}

class CompanyServiceImpl implements ICompanyService {
  @observable currentCompany: ICompany = {} as ICompany;

  constructor(
    @Connect private connect: Connect,
    @Auth private auth: Auth,
  ) {}

  async setCompany() {
    await this.connect.request('COMPANY').then((response) => {
      if (response) {
        this.currentCompany = response.REPLY[0];
      }
    });
  }

  getCurrentCompany(): ICompany {
    return this.currentCompany;
  }

  public async updateCurrentCompany(cascadeCompanyId: string): Promise<any> {
    if (!cascadeCompanyId) return;

    const request: any = await this.connect.commitEvent('EVENT_USER_LAST_LOGIN_UPDATE_COMPANY', {
      DETAILS: { CASCADE_COMPANY_ID: cascadeCompanyId },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  isCreditWorthy(): boolean {
    if (
      (this.currentCompany && this.currentCompany.CREDITWORTHY_INSTITUTION) ||
      this.currentCompany.PARENT_GUARANTEE_FROM_CREDITHWORTHY_INSTITUTION
    ) {
      return true;
    } else {
      return false;
    }
  }

  isInvestmentGrade(): boolean {
    if (
       this.currentCompany && this.currentCompany.CREDIT_PROFILE == CREDIT_PROFILE.INVESTMENT_GRADE
    ) {
        return true;
    } else {
        return false;
    }
  }

  canAutoExecute(): boolean {
    return this.currentCompany?.AUTO_EXECUTION_FLAG;
  }

  clearCompany(): void {
    this.currentCompany = {} as ICompany;
  }

  getAllUserCompanies(): SocketObservable<Message<any>> {
    const request: any = this.connect.stream(
      'USER_COMPANY',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
    );

    return request;
  }

  getAllCompaniesShanpshot(criteria?: string): Promise<Message<any>> {
    return this.connect.snapshot('ALL_COMPANYS', { CRITERIA_MATCH: criteria });
  }

  getAllCompaniesAdmin(criteria: string = ''): Promise<Message<any>> {
    return this.connect.snapshot('ALL_COMPANYS_ADMIN', { CRITERIA_MATCH: criteria });
  }

  public async eventCompanyInsert(company: ICompanyForm): Promise<any> {
    if (!company) return;

    const request: any = await this.connect.commitEvent('EVENT_COMPANY_INSERT', {
      DETAILS: company,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventCompanyUpdate(company: ICompanyForm): Promise<any> {
    if (!company) return;

    const request: any = await this.connect.commitEvent('EVENT_COMPANY_UPDATE', {
      DETAILS: company,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  public async acceptCascadeAgreement(companyId?: string): Promise<any> {
    const request: any = await this.connect.commitEvent('EVENT_COMPANY_AGREEMENT_ACCEPT', {
      DETAILS: {
        CASCADE_COMPANY_ID: companyId ? companyId : this.currentCompany.CASCADE_COMPANY_ID,
      },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  getAllCompanies(criteria?: string): SocketObservable<Message<any>> {
    return this.connect.stream(
      'ALL_COMPANYS',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      { CRITERIA_MATCH: criteria },
    );
  }

  getMyCompanies(criteria?: string): SocketObservable<Message<any>> {
    return this.connect.stream(
      'MY_COMPANIES',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      { CRITERIA_MATCH: criteria },
    );
  }

  public async eventREmapOpen(): Promise<any> {
    await this.setCompany();
    const authUrlDetails = {
      USERNAME: this.auth.currentUser.username,
      COMPANY_ID: this.currentCompany.CASCADE_COMPANY_ID,
    };
    const request: any = await this.connect.commitEvent('EVENT_RESURETY_REMAP_AUTH_URL', {
      DETAILS: authUrlDetails,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  public async getBuyerAdvisorCompany(): Promise<Message<any>> {
    return this.connect.request('BUYER_ADVISOR_COMPANY', { REQUEST: true });
  }

  public async getAdvisorUsersByCompany(
    buyerCompanyId: string,
    advisorCompanyId,
  ): Promise<Message<any>> {
    return this.connect.request('ADVISOR_USERS_BY_COMPANY', {
      REQUEST: { BUYER_COMPANY_ID: buyerCompanyId, ADVISOR_COMPANY_ID: advisorCompanyId },
    });
  }

  public async eventAdvisorUserCompanyUpdate(
    request: IAdvisorUserCompanyUpdateRequest,
  ): Promise<any> {
    return await this.connect.commitEvent('EVENT_UPDATE_ADVISOR_USER_COMPANY_LINKS', {
      DETAILS: request,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
  }

  public async eventAssignCompanyUsers(request: ICompanyUsersRequest): Promise<any> {
    return await this.connect.commitEvent('EVENT_ASSIGN_COMPANY_USERS', {
      DETAILS: request,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
  }

  public async eventUnassignCompanyUsers(request: ICompanyUsersRequest): Promise<any> {
    return await this.connect.commitEvent('EVENT_UNASSIGN_COMPANY_USERS', {
      DETAILS: request,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
  }

  isNonSefParticipantAgreement(): boolean {
    return (
      this.getCurrentCompany()?.NON_SEF_PARTICIPANT_AGREEMENT_CONSENT_DATE !== null &&
      !this.isSefParticipantAgreement()
    );
  }

  isSefParticipantAgreement(): boolean {
    return this.getCurrentCompany()?.SEF_PARTICIPANT_AGREEMENT_CONSENT;
  }
}

export const ICompanyService = DI.createInterface<ICompanyService>((x) =>
  x.singleton(CompanyServiceImpl),
);
